import {ModelSetOptions} from 'backbone';

import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {SinglePolymerMapState} from 'molbio/maps/MapStates';
import Protein from 'molbio/proteins/Protein';
import PolymerMapTabState, {PolymerMapTabStateAttributes} from 'molbio/tabstates/PolymerMapTabState';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';

export interface SinglePolymerMapTabStateAttributes<
  Polymer extends Sequence | Protein,
  MapState extends SinglePolymerMapState<Polymer>,
> extends PolymerMapTabStateAttributes<Polymer> {
  mapstate: MapState;
  bufferViewLayout: BufferViewLayout | null;
}

interface SinglePolymerMapTabState<
  Polymer extends Sequence | Protein,
  MapState extends SinglePolymerMapState<Polymer>,
> {
  get<K extends keyof SinglePolymerMapTabStateAttributes<Polymer, MapState>>(
    key: K
  ): SinglePolymerMapTabStateAttributes<Polymer, MapState>[K];

  set<K extends keyof SinglePolymerMapTabStateAttributes<Polymer, MapState>>(
    attributeName: K,
    value: SinglePolymerMapTabStateAttributes<Polymer, MapState>[K],
    options?: ModelSetOptions
  ): SinglePolymerMapTabState<Polymer, MapState>;

  set(
    obj: Partial<SinglePolymerMapTabStateAttributes<Polymer, MapState>>
  ): SinglePolymerMapTabState<Polymer, MapState>;
}

/**
 * Abstract base class model for a map backed by a single polymer, either Sequence or Protein.
 *
 * Concrete subclasses are expected to implement the abstract `mapState()` method to instantiate a
 * specific subclass of SinglePolymerMapState.
 */
abstract class SinglePolymerMapTabState<
  Polymer extends Sequence | Protein,
  MapState extends SinglePolymerMapState<Polymer>,
> extends PolymerMapTabState<Polymer> {
  abstract mapState(options: {polymer: Polymer; visibilityOptions: VisibilityOptions}): MapState;
  // Optionally define padding at the end of the slice to show features that render between bases (e.g. chemical modifications)
  BASES_PER_SLICE_ADJUSTMENT?: number;

  initialize(attributes?: {}, options?: {}, ...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(attributes, options, ...args);
    if (this.get('mapstate') == null) {
      this.set({
        mapstate: this.mapState({polymer: this.polymer(), visibilityOptions: this.msVisibilityOptions()}),
      });
    }
  }
}

export default SinglePolymerMapTabState;
